import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    Directive, HostBinding, Input
} from "@angular/core";

/**
 * Directive that provides a disabled state binding for host elements.
 *
 * This directive accepts a `disabled` input and binds it to the host element's
 * `disabled` attribute. It is used as a host directive on components that need
 * external disabled state control, and integrates with {@link ProcessingDirective}
 * through its `_disableInjectors` system.
 *
 * Usage as a host directive:
 * ```typescript
 * @Component({
 *     hostDirectives: [
 *         { directive: DisabledDirective, inputs: ["disabled"] }
 *     ]
 * })
 * ```
 *
 * @see {@link ProcessingDirective} for processing state integration
 */
@Directive({
    selector: "[mgdDisabled]"
})
export class DisabledDirective {

    /**
     * Whether the host element is disabled.
     *
     * When set to true, the `disabled` attribute is added to the host element.
     * This value can also be set programmatically by the {@link ProcessingDirective}
     * during application processing states.
     */
    @Input({
        alias: "disabled",
        transform: coerceBooleanProperty
    }) disabled?: boolean = false;

    /**
     * Binds the disabled attribute on the host element.
     * Returns `true` to set the attribute or `null` to remove it,
     * since `attr.disabled="false"` still disables the element in HTML.
     */
    @HostBinding("attr.disabled")
    get attrDisabled(): true | null {
        return this.disabled ? true : null;
    }
}
